import { useState } from "react";
import { Modal } from "@/components/ui/modal";
import { DashboardItinerary } from "@/lib/models/dashboard";
import { useI18n } from '@/hooks/useI18n';
import { SendQuoteChoiceScreen } from "./send-quote/SendQuoteChoiceScreen";
import { SendQuoteEmailFlow } from "./send-quote/SendQuoteEmailFlow";
import { ShareLinkFlow } from "./send-quote/ShareLinkFlow";
import { ShareResultPage } from "./send-quote/ShareResultPage";

interface SendQuoteModalProps {
    isOpen: boolean;
    onClose: () => void;
    itinerary: DashboardItinerary;
    onSuccessClose?: () => void;
}

type FlowState = 'choice' | 'email' | 'link' | 'result';

interface ResultData {
    mode: 'email' | 'link';
    pdfBase64?: string;
    pdfFilename?: string;
    emailSent?: boolean;
    shareUrl?: string;
    linkPassword?: string;
    itinerary: DashboardItinerary;
}

export const SendQuoteModal = ({
    isOpen,
    onClose,
    itinerary,
    onSuccessClose
}: SendQuoteModalProps) => {
    const { t } = useI18n();
    const [flowState, setFlowState] = useState<FlowState>('choice');
    const [resultData, setResultData] = useState<ResultData | null>(null);
    // Container node for the sticky footer rendered by the Modal. Flows portal
    // their navigation buttons into it so the footer sits OUTSIDE the scroll area
    // (fixed like the header, and the scrollbar covers only the body).
    const [footerContainer, setFooterContainer] = useState<HTMLDivElement | null>(null);

    const flowNeedsFooter = flowState === 'email' || flowState === 'link';

    const handleSelectEmailFlow = () => {
        setFlowState('email');
    };

    const handleSelectLinkFlow = () => {
        setFlowState('link');
    };

    const handleBackToChoice = () => {
        setFlowState('choice');
    };

    const handleEmailFlowComplete = (data: { pdfBase64?: string; pdfFilename?: string; emailSent: boolean }) => {
        setResultData({
            mode: 'email',
            pdfBase64: data.pdfBase64,
            pdfFilename: data.pdfFilename,
            emailSent: data.emailSent,
            itinerary,
        });
        setFlowState('result');
    };

    const handleLinkFlowComplete = (data: { shareUrl: string; linkPassword?: string; itinerary: DashboardItinerary }) => {
        setResultData({
            mode: 'link',
            shareUrl: data.shareUrl,
            linkPassword: data.linkPassword,
            itinerary: data.itinerary,
        });
        setFlowState('result');
    };

    const handleClose = () => {
        const wasSuccess = flowState === 'result';
        setFlowState('choice');
        setResultData(null);
        onClose();
        if (wasSuccess) onSuccessClose?.();
    };

    const getModalTitle = () => {
        switch (flowState) {
            case 'choice':
                return t('itinerary.shareItinerary');
            case 'email':
                return t('itinerary.sendQuoteViaEmail');
            case 'link':
                return t('itinerary.generateShareLink');
            case 'result':
                return t('common.success');
            default:
                return t('itinerary.shareItinerary');
        }
    };

    const renderContent = () => {
        switch (flowState) {
            case 'choice':
                return (
                    <SendQuoteChoiceScreen
                        onSelectEmailFlow={handleSelectEmailFlow}
                        onSelectLinkFlow={handleSelectLinkFlow}
                    />
                );
            case 'email':
                return (
                    <SendQuoteEmailFlow
                        itinerary={itinerary}
                        onBack={handleBackToChoice}
                        onComplete={handleEmailFlowComplete}
                        footerContainer={footerContainer}
                    />
                );
            case 'link':
                return (
                    <ShareLinkFlow
                        initialItinerary={itinerary}
                        onBack={handleBackToChoice}
                        onComplete={handleLinkFlowComplete}
                        footerContainer={footerContainer}
                    />
                );
            case 'result':
                return resultData ? (
                    <ShareResultPage
                        mode={resultData.mode}
                        itinerary={resultData.itinerary}
                        pdfBase64={resultData.pdfBase64}
                        pdfFilename={resultData.pdfFilename}
                        emailSent={resultData.emailSent}
                        shareUrl={resultData.shareUrl}
                        linkPassword={resultData.linkPassword}
                        onClose={handleClose}
                    />
                ) : null;
            default:
                return null;
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={getModalTitle()}
            size="lg"
            footer={flowNeedsFooter ? <div ref={setFooterContainer} className="flex w-full items-center justify-between" /> : undefined}
        >
            {renderContent()}
        </Modal>
    );
};